<template>
  <div>
    <!-- 레이어 팝업 -->
    <PopupShare
      v-if="shareLayout"
      :id="programid"
      :title="contentInfo.programtitle"
      :description="`${contentInfo.episodenumber} 회 ${contentInfo.episodetitle}`"
      @on-click-close="closeShareLayout"
    />

    <!-- 콘텐츠 영역 -->
    <template v-if="!isLoading">
      <main id="contents" class="vod player-wrap">
        <!-- 플레이어 영역 -->
        <VodPlayer
          v-if="hasContentId"
          :key="'vod_player'"
          :content-info="contentInfo"
          :landing-info="landingInfo"
          :open-share-popup="openShareLayout"
        />

        <EventBannerList
          v-if="hasEventList && !isPooqzone"
          :event-banner-list="eventBannerList"
        />

        <BottomTabContainer
          :key="`bottom_tab_container`"
          :content-info="contentInfo"
          :landing-info="landingInfo"
        />
      </main>
      <MetaInfo :content-info="contentInfo" content-type="vod" />
    </template>

    <div v-else class="min-h-screen" />
  </div>
</template>

<script>
  import { parseUrl } from 'query-string'
  import Swiper from '@/mixins/swiper'
  import _ from 'lodash'
  import { mapMutations } from 'vuex'
  import cookie from '@/common/utils/cookie'
  import {
    SET_CURRENT_MENU,
    MENU,
    GENRE_CODE_GLOBAL_DRAMA,
  } from '@/common/resources/const'
  import {
    destroyPlayer,
    redirectToHttps,
    scrollTop,
    scrollTopWithCallback,
  } from '@/common/utils/common'
  import {
    checkAdultContent,
    openAdultPopupIfNeeded,
  } from '@/common/utils/adult'
  import {
    getEventsBannerVod,
    getVodContentsDetail,
    getVodLandingInfo,
  } from '@/common/utils/request'
  import { setPlayerModeDefault } from '@/common/utils/scalable.player'
  import { multiProfileAutoLogin, autoLoginSetting } from '@/common/utils/login'
  import playerPage from '@/mixins/player.page.mixin'
  import PopupShare from '@/components/player/PopupShare'
  import VodPlayer from '@/components/player/VodPlayer'
  import MetaInfo from '@/components/meta/Meta'
  import BottomTabContainer from '@/components/player/tabs/BottomTabContainer'
  import EventBannerList from '@/components/player/EventBannerList'
  import braze from '@/common/utils/braze'
  import { getPurchaseProperties } from '@/common/utils/braze.util'

  export default {
    name: 'Vod',
    components: {
      EventBannerList,
      BottomTabContainer,
      PopupShare,
      VodPlayer,
      MetaInfo,
    },
    mixins: [Swiper, playerPage],
    data() {
      return {
        isLoading: true,
        componentKey: 0,
        playerUpdateKey: 0,
        type: 'vod',
        contentid: '',
        programid: '',
        contentInfo: {}, // 콘텐츠 데이터
        landingInfo: {},
        order: 'new',
        landingContentHistory: '',
        isPooqzone: cookie.isWavveOn(),
        eventBannerList: [],
      }
    },
    computed: {
      hasContentId() {
        return _.get(this.contentInfo, 'contentid', '') !== ''
      },
      hasEventList() {
        return this.eventBannerList.length > 0
      },
    },
    watch: {
      $route(n, o) {
        this.initialize()
      },
    },
    created() {
      window.addEventListener('message', this.postMessage)
      scrollTop()
      redirectToHttps()
      this.initialize()
    },
    beforeDestroy() {
      destroyPlayer()
      window.removeEventListener('message', this.postMessage)
    },
    methods: {
      ...mapMutations([SET_CURRENT_MENU]),
      get: _.get,
      errorCatcher(error) {
        const message =
          error?.response?.data?.resultmessage ?? '해당 데이터가 없습니다.'
        alert(message)
        this.$router.back()
      },
      setActiveGnbMenuByContentGenre(genreCode) {
        if (genreCode === GENRE_CODE_GLOBAL_DRAMA) {
          this[SET_CURRENT_MENU](MENU.GLOBAL)
        } else {
          this[SET_CURRENT_MENU](MENU.BROADCAST)
        }
      },
      async setData() {
        this.isLoading = true

        // set vod data
        try {
          const { data } = await getVodContentsDetail({
            contentid: this.contentid,
          })
          const { isAdultVerified } = await checkAdultContent(data)
          await openAdultPopupIfNeeded({ isAdultVerified })
          this.setActiveGnbMenuByContentGenre(_.get(data, 'genrevalue', ''))
          this.contentInfo = data

          if (_.get(this.$route, 'query.order', '') === '')
            this.updateOrder(_.get(data, 'episodeorder', 'new'))
          if (!this.programid) this.programid = this.contentInfo.programid
          this.isLoading = !isAdultVerified
          scrollTopWithCallback(setPlayerModeDefault)
        } catch (e) {
          this.errorCatcher(e)
        }

        // set vod event banner
        try {
          const { data } = await getEventsBannerVod({
            programid: this.programid,
          })
          this.eventBannerList = data?.list ?? []
        } catch (error) {
          console.error(error)
        }
      },
      async setLandingInfo() {
        try {
          const { data } = await getVodLandingInfo({
            programid: this.programid,
            contentid: this.contentid,
            history: this.landingContentHistory,
          })
          const { content_id: contentId = '' } = data
          this.landingInfo = data
          this.contentid = contentId
          await this.setData()
        } catch (e) {
          this.errorCatcher(e)
        }
      },
      initialize() {
        const { query } = parseUrl(location.href)
        const { landing = '', history = '' } = query
        this.programid = _.get(query, 'programid', '')
        this.contentid = _.get(query, 'contentid', '')
        this.landingContentHistory = landing || history || 'all'
        this.order = _.get(this.$route, 'query.order', 'new')
        this.setLandingInfo()
      },
      updateOrder(order) {
        this.order = order
      },
      async postMessage(e) {
        if (e.origin === location.origin) {
          const eventMessage = _.get(e, 'data.eventMessage', e.data)
          if (eventMessage === 'voucherAutoLogin') {
            const credential = cookie.getCredential()
            const isMultiLine = Number(cookie.userInfo('profilecount')) !== 1
            autoLoginSetting(credential, isMultiLine).then(() => {
              location.reload()
            })
          }
          if (eventMessage === 'purchaseComplete') {
            if (e.data?.params?.result === 'y') {
              braze.purchase(getPurchaseProperties(e.data?.params))
            }
            await multiProfileAutoLogin()
            await this.setLandingInfo()
          }
        }
      },
    },
  }
</script>
<style lang="scss" src="@/assets/scss/category/vod.scss" />
